import { toRaw, type InjectionKey, inject } from 'vue';
import type { UrlGenerator } from './UrlGenerator.ts';

export const RouterInjectionKey = Symbol('AppRouter') as InjectionKey<Router>;

type Params = Record<string, any>;

export type Route = {
  uri: string;
  domain: string;
  params: string[];
  binding: Record<string, string>;
}

export type RouterSettings = {
  locale: string;
  fallback: string;
  available: string[];
  routes: Record<string, Route>;
}

export class Router {
  private readonly generator: UrlGenerator;

  private readonly locale: string;
  private readonly fallback: string;
  private readonly available: string[];
  private readonly routes: Record<string, Route>;

  public constructor(generator: UrlGenerator, settings: RouterSettings) {
    this.generator = generator;
    this.locale = settings.locale;
    this.fallback = settings.fallback;
    this.available = settings.available;
    this.routes = settings.routes;
  }

  public route(name: string, params: Params = {}, hash?: string) {
    return this.build(this.localizeName(name), params, hash);
  }

  private build(name: string, params: Params, hash?: string) {
    const route = this.routes[name];

    if (!route) {
      throw new Error(`Undefined route: ${name}`);
    }

    const uri = this.replaceRouteParameters(route, params);

    const search = Object.keys(params).reduce((prev, key) => {
      if (!route.params.includes(key)) {
        prev[key] = toRaw(params[key]);
      }

      return prev;
    }, {} as Record<string, any>);

    return this.generator.generate(uri, search, hash, route.domain);
  }

  private localizeName(name: string) {
    let currentLocale = this.locale;
    let fallbackLocale = this.fallback;
    let availableLocales = this.available;

    if (name.startsWith(fallbackLocale)) {
      return name.replace(`${fallbackLocale}.`, '');
    }

    if (availableLocales.findIndex(lang => name.startsWith(lang)) >= 0) {
      return name;
    }

    if (!name.startsWith('web.')) {
      return name;
    }

    if (currentLocale !== fallbackLocale) {
      return `${currentLocale}.${name}`;
    }

    return name;
  }

  private replaceRouteParameters(route: Route, params: Params) {
    return route.params.reduce((uri, param) => {
      let binding = route.binding[param] || 'id';
      let value = toRaw(params[param]);

      if (typeof value === 'object') {
        value = value[binding];
      }

      if (!value) {
        if (!uri.match(new RegExp(`\{${param}\\?\}`))) {
          throw new Error(`Parameter ${param} is required for uri ${route.uri}.`);
        }
      }

      uri = uri.replace(new RegExp(`\{${param}\\??\}`), value ?? '');

      if (uri.endsWith('/')) {
        uri = uri.slice(0, -1);
      }

      return uri;
    }, route.uri);
  }
}

export function useRouter() {
  return inject(RouterInjectionKey)!;
}

export function useRoute() {
  const router = useRouter();

  return router.route.bind(router);
}

/**
 * @deprecated Use `useRouter()` instead in your components.
 */
export function route(name: string, params: Params = {}, hash?: string) {
  return useRouter().route(name, params, hash);
}
